// Small data-loading helpers. Deliberately tiny: every page has the same shape
// (pick a period → fetch → render), and a shared hook keeps the loading/error handling
// identical instead of six slightly different versions of it.

import { useCallback, useEffect, useState } from 'react';
import { ApiError } from './api';
import type { Meta, PeriodOption } from './types';

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string;
  reload: () => void;
}

/**
 * Run `fn` whenever `deps` change. A request that resolves after a newer one started is
 * DISCARDED (the `cancelled` flag) — otherwise switching periods quickly can leave the
 * slower, older response on screen under the newer period's heading.
 */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fn()
      .then((v) => { if (!cancelled) setData(v); })
      .catch((err) => {
        if (cancelled) return;
        // A 401 is already handled globally (App.tsx returns to login) — showing an error
        // box behind the login screen would just be noise.
        if (err instanceof ApiError && err.status === 401) return;
        // Drop whatever the PREVIOUS request returned. Keeping it while the page renders an
        // error left the old entity's name in the heading — "Vegas · Kalite Raporu" above
        // "Kayıt bulunamadı." after navigating to a brand that does not exist, and the
        // previous agent's name above an unknown agent id. A failed load has no data.
        setData(null);
        setError(err instanceof ApiError ? err.message : 'Veri alınamadı.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, loading, error, reload };
}

// 'all' = no period window at all (the evaluations LIST default): the picker sends an empty
// id, the server applies no call_at filter, and every in-scope row is returned newest-first.
// The weekly/monthly REPORT screens never offer it — a report is defined by its period.
// 'day' (owner, 2026-09-12) is the list's finest filter — a single Istanbul calendar day.
export type PeriodKind = 'day' | 'week' | 'month' | 'all';

export interface PeriodPicker {
  kind: PeriodKind;
  setKind: (k: PeriodKind) => void;
  id: string;
  setId: (id: string) => void;
  options: PeriodOption[];
}

/**
 * Weekly/monthly toggle + the period dropdown. Switching kind resets to the CURRENT period
 * of that kind rather than trying to map week 34 onto a month, which has no meaning.
 */
export function usePeriodPicker(meta: Meta | null, initial: PeriodKind = 'week'): PeriodPicker {
  const [kind, setKindState] = useState<PeriodKind>(initial);
  const [id, setId] = useState<string>('');

  // 'all' has no options and an empty id (no window). day/week/month list their periods.
  const options = kind === 'day' ? (meta?.periods.days ?? [])
    : kind === 'week' ? (meta?.periods.weeks ?? [])
      : kind === 'month' ? (meta?.periods.months ?? [])
        : [];

  // Default to the newest option as soon as meta arrives, and whenever the kind changes.
  // In 'all' mode options is empty, so this no-ops and the empty id stands.
  useEffect(() => {
    if (options.length && !options.some((o) => o.id === id)) setId(options[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, options.length]);

  const setKind = useCallback((k: PeriodKind) => { setKindState(k); setId(''); }, []);
  return { kind, setKind, id, setId, options };
}
